import React, { FC, useState, useRef, useEffect } from "react";
import { RouterLink } from "../../components/RouterLink";
import { Header } from "../../components/Header";
import { Tabs, Button, message } from "antd";
import { Container } from "../../components/Container";
import styles from "./styles.module.less";
import { createForm, FormInstance } from "../../hooks/createForm";
import { PhoneCodeLoginForm } from "../../components/forms/login/PhoneCodeLoginForm";
import { AuthingTabs } from "../../components/AuthingTabs/index";
import { SocialLogin } from "../../components/SocialLogin/index";
import { QrCodeForm } from "../../components/forms/login/QrCodeForm/index";
import { User } from "authing-js-sdk";
import { getDisplayName } from "../../utils";
import { Protocol } from "../../config/index";
import querystring from "query-string";
import { PasswordLoginForm } from "../../components/forms/login/PasswordLoginForm/index";
import { useTitle } from "../../hooks/useTitle";
import { useNavigateWithSearch } from "../../hooks/useNavigateWithSearch";
import { MFALoginForm } from "../../components/forms/login/MFALoginForm";
import classnames from "classnames";
import { noviceEventBus } from "../../components/Guidance/utils";
import { LdapLoginForm } from "../../components/forms/login/LdapLoginForm";
import { ADLoginForm } from "../../components/forms/login/ADLoginForm";
import { useTranslation } from "react-i18next";
import { ToggleLang } from "@/components/ToggleLang";
import { LOGIN_METHODS_MAP } from "@/constants/enum";

const { TabPane } = Tabs;

const getDefaultTab = (tab: string, tabs: string[]) => {
  return tabs.find((t) => t === tab) || tabs[0];
};

const getQueryMFAToken = () => {
  const queryVal = querystring.parse(window.location.search).mfa_token;
  if (typeof queryVal === "string") {
    return queryVal;
  }
  if (Array.isArray(queryVal)) {
    return queryVal.filter((item) => item)[0] || "";
  }
  return "";
};

export const LoginPage: FC = () => {
  const { t } = useTranslation();

  useTitle(t("common.login"));

  type FormRef = React.RefObject<FormInstance>;

  const config = window.__config__;
  const initMFAToken = getQueryMFAToken();

  const formsRef = useRef<{ [key: string]: FormRef }>({});
  const [activeTabKey, setActiveTabKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState<{ [key: string]: string }>({});
  const navigate = useNavigateWithSearch();

  const [isMFA, setIsMFA] = useState(Boolean(initMFAToken));
  const [MFAToken, setMFAToken] = useState(initMFAToken);
  const mfaFormRef = createForm();

  const [formHidden, setFormHidden] = useState({
    action: "",
    token: "",
  });
  const submitBtn = useRef<any>();

  useEffect(() => {
    const query = querystring.parse(window.location.search);
    const path = query["goto"];
    if (path) {
      navigate(path as string);
    }
    const tab = query["tab"] as string;
    setActiveTabKey(getDefaultTab(tab || config.loginTabs.default, config.loginTabs.list));
  }, [config.loginTabs.default, config.loginTabs.list, navigate]);

  const submit = async () => {
    if (loading) return;
    // 提交表单
    if (isMFA) {
      mfaFormRef.current?.submit();
      return;
    }
    formsRef.current[activeTabKey]?.current?.submit();
  };

  const onSuccess = async (user: User) => {
    setLoading(false);
    await window.__authing__.setCurrentUser(user);

    message.success(
      t("common.loginSuccessWelcome", {
        name: getDisplayName(user),
      })
    );
    setTimeout(() => {
      // TODO: 这段逻辑与 src/utils/loginSuccess.tsx 的 loginSuccessRedirect 一致，谁敢替换一下
      if (config.protocol === Protocol.OIDC) {
        const query = querystring.parse(window.location.search);
        setFormHidden({
          action: `/interaction/oidc/${query["uuid"]}/login`,
          token: user.token ?? "",
        });
        submitBtn.current.click();
        return;
      }

      if (config.protocol === Protocol.SAML) {
        setFormHidden({
          action: `/interaction/saml-idp/login`,
          token: user.token ?? "",
        });
        submitBtn.current.click();
        return;
      }

      if (config.protocol === Protocol.OAUTH) {
        setFormHidden({
          action: `/interaction/oauth/login`,
          token: user.token ?? "",
        });
        setFields(querystring.parse(window.location.search) as any);
        submitBtn.current.click();
        return;
      }

      // protocol=none 时，直接带着用户信息跳转过去
      let redirectUrl = `${config.redirectUris && config.redirectUris[0]}/?data=${encodeURIComponent(
        JSON.stringify(user)
      )}`;
      window.location.href = redirectUrl;
    }, 500);
  };

  const onFail = (error: any) => {
    setLoading(false);
    if (error.code === 1635) {
      setIsMFA(true);
      setMFAToken(error.data.mfaToken);
      return;
    }
    console.log("登录失败:\n", error);
  };

  const formFactories: { [key: string]: (ref: FormRef) => JSX.Element } = {
    password: (ref) => (
      <PasswordLoginForm ref={ref} onSubmit={() => setLoading(true)} onSuccess={onSuccess} onFail={onFail} />
    ),
    ldap: (ref) => <LdapLoginForm ref={ref} onSubmit={() => setLoading(true)} onSuccess={onSuccess} onFail={onFail} />,
    ad: (ref) => <ADLoginForm ref={ref} onSubmit={() => setLoading(true)} onSuccess={onSuccess} onFail={onFail} />,
    "phone-code": (ref) => (
      <PhoneCodeLoginForm ref={ref} onSubmit={() => setLoading(true)} onSuccess={onSuccess} onFail={onFail} />
    ),
    "wechat-miniprogram-qrcode": (ref) => (
      <QrCodeForm ref={ref} onSuccess={onSuccess} onFail={onFail} type="wechat-miniprogram-qrcode" />
    ),
    MFA: (ref) => (
      <MFALoginForm
        ref={ref}
        MFAToken={MFAToken}
        onSubmit={() => setLoading(true)}
        onSuccess={onSuccess}
        onFail={onFail}
      />
    ),
    "app-qrcode": (ref) => <QrCodeForm ref={ref} onSuccess={onSuccess} onFail={onFail} type="app-qrcode" />,
  };

  const tabs = config.loginTabs.list
    // 过滤掉不支持的方式
    .filter((tab) => Object.keys(formFactories).includes(tab));
  const tabPanes = tabs.map((tab) => {
    const title = LOGIN_METHODS_MAP[tab];
    const ref = (formsRef.current[tab] = createForm());
    const form = formFactories[tab](ref);
    return (
      <TabPane tab={title} key={tab}>
        {form}
      </TabPane>
    );
  });

  const noForm = !config.ssoPageComponentDisplay.loginMethodNav || tabPanes.length === 0;

  const loginElements = !noForm && activeTabKey !== "wechat-miniprogram-qrcode" && activeTabKey !== "app-qrcode" && (
    <>
      {config.ssoPageComponentDisplay.loginBtn && (
        <Button
          data-novice-focus="7"
          data-novice-action="7"
          loading={loading}
          className={styles.registerBtn}
          block
          size="large"
          type="primary"
          onClick={submit}
        >
          {t("common.login")}
        </Button>
      )}

      <div className={styles.gotoRegister}>
        {config.ssoPageComponentDisplay.forgetPasswordBtn && !["ad", "ldap"].includes(activeTabKey) && (
          <RouterLink to="/reset-password" className="__authing-forget-password">
            {t("common.hasForgetPwd")}
          </RouterLink>
        )}
        {config.ssoPageComponentDisplay.registerBtn && !["ad", "ldap"].includes(activeTabKey) && (
          <span>
            {t("common.noAccYet")}
            <RouterLink
              onClick={() => noviceEventBus.emit("next")}
              data-novice-focus="5"
              data-novice-action="5"
              to="/register"
            >
              {t("common.registerImmediate")}
            </RouterLink>
          </span>
        )}
      </div>
      <div style={{ marginBottom: 26 }}></div>

      <div style={{ flex: 1 }}></div>
    </>
  );

  const MFAElements = (
    <>
      <Button
        loading={loading}
        className={classnames(styles.mfaLoginBtn, styles.registerBtn)}
        block
        size="large"
        type="primary"
        onClick={submit}
      >
        {t("common.sure")}
      </Button>
      <span className={styles.mfaForgetTips}>
        {t("common.hasLooseSaftyCode")}
        <RouterLink
          to={{
            pathname: "/reset-mfa?",
            search: querystring.stringify(
              Object.assign(querystring.parse(window.location.search), {
                mfa_token: MFAToken,
              })
            ),
          }}
          className="__authing-forget-password"
        >
          {t("common.useRecoverCode")}
        </RouterLink>
      </span>
    </>
  );

  return (
    <Container minHeight={noForm ? "auto" : undefined}>
      <Header title={config.name} logo={config.logo} />
      {config.ssoPageComponentDisplay.loginMethodNav && !isMFA && (
        <AuthingTabs centered activeKey={activeTabKey} onChange={setActiveTabKey}>
          {tabPanes}
        </AuthingTabs>
      )}

      {isMFA && formFactories.MFA(mfaFormRef)}

      {isMFA ? MFAElements : loginElements}
      {!isMFA && <SocialLogin noForm={noForm} onFail={onFail} onSuccess={onSuccess}></SocialLogin>}

      <form action={formHidden.action} method="post" style={{ display: "none" }}>
        {Object.entries(fields).map(([k, v]) => (
          <input key={k} type="text" name={k} value={v} readOnly />
        ))}
        <input type="text" name="token" value={formHidden.token} readOnly />
        <button ref={submitBtn} type="submit"></button>
      </form>
      <div className={styles.layoutFlex}>
        <ToggleLang />
        <div
          className={styles.problem}
          onClick={() => {
            window.location.href = "/feedback";
          }}
        >
          {t("common.quesitons")}
        </div>
      </div>
    </Container>
  );
};
